import { useState } from 'react';

interface CalcResult {
  method: string;
  icon: string;
  color: string;
  price: string;
  time: string;
  available: boolean;
  note?: string;
}

const cityZones: Record<string, { zone: string; label: string }> = {
  'москва': { zone: 'moscow_mkad', label: 'Москва (в пределах МКАД)' },
  'москва мо': { zone: 'moscow_mo', label: 'Московская область' },
  'санкт-петербург': { zone: 'spb', label: 'Санкт-Петербург' },
  'спб': { zone: 'spb', label: 'Санкт-Петербург' },
  'питер': { zone: 'spb', label: 'Санкт-Петербург' },
  'казань': { zone: 'million', label: 'Казань' },
  'екатеринбург': { zone: 'million', label: 'Екатеринбург' },
  'новосибирск': { zone: 'million', label: 'Новосибирск' },
  'нижний новгород': { zone: 'million', label: 'Нижний Новгород' },
  'краснодар': { zone: 'million', label: 'Краснодар' },
  'ростов-на-дону': { zone: 'million', label: 'Ростов-на-Дону' },
  'уфа': { zone: 'million', label: 'Уфа' },
  'самара': { zone: 'million', label: 'Самара' },
  'омск': { zone: 'million', label: 'Омск' },
  'челябинск': { zone: 'million', label: 'Челябинск' },
  'воронеж': { zone: 'other', label: 'Воронеж' },
  'пермь': { zone: 'million', label: 'Пермь' },
  'красноярск': { zone: 'million', label: 'Красноярск' },
};

const calcResults = (zone: string, orderSum: number): CalcResult[] => {
  const isMoscowMkad = zone === 'moscow_mkad';
  const isMoscowMo = zone === 'moscow_mo';
  const isSpb = zone === 'spb';
  const isMillion = zone === 'million';
  const isOther = zone === 'other' || zone === 'unknown';

  const expressAvail = isMoscowMkad || isSpb;
  const standardAvail = isMoscowMkad || isMoscowMo || isSpb || isMillion;

  const expressPrice = (() => {
    if (!expressAvail) return null;
    return 199;
  })();

  const standardPrice = (() => {
    if (!standardAvail) return null;
    if (isMoscowMkad || isSpb) return orderSum >= 500 ? 0 : 99;
    if (isMoscowMo) return orderSum >= 1500 ? 0 : 199;
    if (isMillion) return orderSum >= 2000 ? 0 : 249;
    return null;
  })();

  const postPrice = (() => {
    if (isMoscowMkad || isSpb) return null;
    if (isMoscowMo) return orderSum >= 2000 ? 0 : 250;
    if (isMillion) return orderSum >= 2000 ? 0 : 300;
    return orderSum >= 2000 ? 0 : 350;
  })();

  return [
    {
      method: 'Экспресс-доставка',
      icon: 'ri-flashlight-line',
      color: '#ff2d78',
      price: expressAvail ? (expressPrice === 0 ? 'Бесплатно' : `${expressPrice} ₽`) : 'Недоступно',
      time: '15–60 мин',
      available: expressAvail,
      note: expressAvail ? 'Круглосуточно, 24/7' : 'Только Москва (МКАД) и СПб',
    },
    {
      method: 'Стандартная доставка',
      icon: 'ri-truck-line',
      color: '#00f5ff',
      price: standardAvail
        ? standardPrice === 0
          ? 'Бесплатно'
          : `${standardPrice} ₽`
        : 'Недоступно',
      time: '2–4 часа',
      available: standardAvail,
      note: standardAvail
        ? standardPrice === 0
          ? 'Бесплатно для вашего заказа!'
          : `Бесплатно от ${isMoscowMkad || isSpb ? 500 : isMoscowMo ? 1500 : 2000} ₽`
        : 'Недоступно в вашем регионе',
    },
    {
      method: 'Самовывоз из аптеки',
      icon: 'ri-store-2-line',
      color: '#39ff14',
      price: 'Бесплатно',
      time: '1 час',
      available: true,
      note: 'Более 1 000 аптек по всей России',
    },
    {
      method: 'Почта России / СДЭК',
      icon: 'ri-mail-send-line',
      color: '#f0ff00',
      price:
        postPrice === null
          ? 'Недоступно'
          : postPrice === 0
          ? 'Бесплатно'
          : `от ${postPrice} ₽`,
      time: '3–10 дней',
      available: postPrice !== null,
      note:
        postPrice === null
          ? 'Используйте курьерскую доставку'
          : postPrice === 0
          ? 'Бесплатно для вашего заказа!'
          : 'Бесплатно от 2 000 ₽',
    },
  ];
};

const popularCities = ['Москва', 'Санкт-Петербург', 'Казань', 'Екатеринбург', 'Новосибирск', 'Краснодар'];

const DeliveryCalculator = () => {
  const [city, setCity] = useState('');
  const [orderSum, setOrderSum] = useState('');
  const [results, setResults] = useState<CalcResult[] | null>(null);
  const [detectedZone, setDetectedZone] = useState<string | null>(null);
  const [error, setError] = useState('');

  const handleCalculate = () => {
    const cityKey = city.trim().toLowerCase();
    const sum = parseFloat(orderSum) || 0;

    if (!cityKey) {
      setError('Введите город доставки');
      return;
    }

    setError('');

    const found = cityZones[cityKey];
    const zone = found ? found.zone : 'unknown';
    setDetectedZone(found ? found.label : city);
    setResults(calcResults(zone, sum));
  };

  const handleCitySelect = (c: string) => {
    setCity(c);
    setResults(null);
    setDetectedZone(null);
    setError('');
  };

  const handleSumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/[^0-9]/g, '');
    setOrderSum(val);
    if (results) setResults(null);
  };

  return (
    <section className="px-6 lg:px-12 pb-16">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-10">
          <p className="font-mono text-xs tracking-[0.3em] text-neon-cyan/60 mb-2">КАЛЬКУЛЯТОР</p>
          <h2 className="font-orbitron font-black text-2xl md:text-3xl text-white">РАССЧИТАТЬ ДОСТАВКУ</h2>
          <div className="mt-2 w-12 h-0.5" style={{ background: 'linear-gradient(90deg, #ff2d78, transparent)' }} />
          <p className="font-mono text-xs text-white/35 mt-3 max-w-xl leading-relaxed">
            Введите город и сумму заказа, чтобы узнать стоимость и сроки доставки для вашего региона.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Input form */}
          <div
            className="rounded-2xl p-6"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div className="space-y-5">
              {/* City input */}
              <div>
                <label className="block font-mono text-xs text-white/40 mb-2 tracking-wider">ГОРОД ДОСТАВКИ</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-map-pin-line text-sm" style={{ color: '#ff2d78' }} />
                  </div>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => { setCity(e.target.value); setResults(null); setError(''); }}
                    placeholder="Например: Москва"
                    className="w-full pl-9 pr-4 py-3 rounded-xl font-mono text-sm text-white placeholder-white/20 outline-none transition-all"
                    style={{
                      background: 'rgba(255,255,255,0.04)',
                      border: `1px solid ${error ? 'rgba(255,45,120,0.5)' : 'rgba(255,255,255,0.1)'}`,
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(255,45,120,0.5)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = error ? 'rgba(255,45,120,0.5)' : 'rgba(255,255,255,0.1)'; }}
                  />
                </div>
                {error && <p className="font-mono text-[10px] mt-1.5" style={{ color: '#ff2d78' }}>{error}</p>}

                {/* Popular cities */}
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {popularCities.map((c) => (
                    <button
                      key={c}
                      onClick={() => handleCitySelect(c)}
                      className="whitespace-nowrap px-2.5 py-1 rounded-full font-mono text-[10px] cursor-pointer transition-all"
                      style={{
                        background: city === c ? 'rgba(255,45,120,0.15)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${city === c ? 'rgba(255,45,120,0.4)' : 'rgba(255,255,255,0.08)'}`,
                        color: city === c ? '#ff2d78' : 'rgba(255,255,255,0.35)',
                      }}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Order sum */}
              <div>
                <label className="block font-mono text-xs text-white/40 mb-2 tracking-wider">СУММА ЗАКАЗА (₽)</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 flex items-center justify-center">
                    <i className="ri-money-cny-circle-line text-sm" style={{ color: '#39ff14' }} />
                  </div>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={orderSum}
                    onChange={handleSumChange}
                    placeholder="0"
                    className="w-full pl-9 pr-4 py-3 rounded-xl font-mono text-sm text-white placeholder-white/20 outline-none transition-all"
                    style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = 'rgba(57,255,20,0.5)'; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)'; }}
                  />
                  {orderSum && (
                    <div className="absolute right-3 top-1/2 -translate-y-1/2 font-mono text-xs text-white/30">₽</div>
                  )}
                </div>
                <p className="font-mono text-[10px] text-white/25 mt-1.5">Укажите сумму для расчёта бесплатной доставки</p>
              </div>

              {/* Quick sum buttons */}
              <div>
                <p className="font-mono text-[10px] text-white/25 mb-2">БЫСТРЫЙ ВЫБОР СУММЫ</p>
                <div className="flex flex-wrap gap-2">
                  {[300, 500, 1000, 1500, 2000, 3000].map((s) => (
                    <button
                      key={s}
                      onClick={() => { setOrderSum(String(s)); setResults(null); }}
                      className="whitespace-nowrap px-3 py-1.5 rounded-lg font-mono text-xs cursor-pointer transition-all"
                      style={{
                        background: orderSum === String(s) ? 'rgba(0,245,255,0.12)' : 'rgba(255,255,255,0.04)',
                        border: `1px solid ${orderSum === String(s) ? 'rgba(0,245,255,0.4)' : 'rgba(255,255,255,0.08)'}`,
                        color: orderSum === String(s) ? '#00f5ff' : 'rgba(255,255,255,0.4)',
                      }}
                    >
                      {s.toLocaleString('ru')} ₽
                    </button>
                  ))}
                </div>
              </div>

              {/* Calculate button */}
              <button
                onClick={handleCalculate}
                className="whitespace-nowrap w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-orbitron font-bold text-sm tracking-wider cursor-pointer transition-all"
                style={{ background: 'linear-gradient(135deg, #ff2d78, #ff6b00)', color: '#fff', boxShadow: '0 0 20px rgba(255,45,120,0.35)' }}
              >
                <i className="ri-calculator-line" />
                РАССЧИТАТЬ СТОИМОСТЬ
              </button>
            </div>
          </div>

          {/* Results */}
          <div>
            {!results ? (
              <div
                className="h-full rounded-2xl flex flex-col items-center justify-center p-8 text-center"
                style={{ background: 'rgba(255,255,255,0.015)', border: '1px dashed rgba(255,255,255,0.08)' }}
              >
                <div className="w-16 h-16 flex items-center justify-center rounded-2xl mb-4" style={{ background: 'rgba(0,245,255,0.08)', border: '1px solid rgba(0,245,255,0.2)' }}>
                  <i className="ri-truck-line text-3xl" style={{ color: '#00f5ff', opacity: 0.5 }} />
                </div>
                <p className="font-orbitron font-bold text-sm text-white/30 mb-2">Введите данные</p>
                <p className="font-mono text-xs text-white/20 leading-relaxed max-w-xs">
                  Укажите город и сумму заказа, чтобы увидеть доступные варианты доставки и их стоимость
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Detected zone */}
                <div
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl"
                  style={{ background: 'rgba(0,245,255,0.06)', border: '1px solid rgba(0,245,255,0.2)' }}
                >
                  <i className="ri-map-pin-2-line text-sm" style={{ color: '#00f5ff' }} />
                  <span className="font-mono text-xs text-white/60">Зона доставки:</span>
                  <span className="font-mono text-xs font-bold" style={{ color: '#00f5ff' }}>{detectedZone}</span>
                  {orderSum && (
                    <>
                      <span className="font-mono text-xs text-white/30 ml-auto">Заказ:</span>
                      <span className="font-mono text-xs text-white/60">{parseInt(orderSum).toLocaleString('ru')} ₽</span>
                    </>
                  )}
                </div>

                {results.map((r) => (
                  <div
                    key={r.method}
                    className="rounded-xl p-4 transition-all duration-300"
                    style={{
                      background: r.available ? `${r.color}06` : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${r.available ? r.color + '25' : 'rgba(255,255,255,0.06)'}`,
                      opacity: r.available ? 1 : 0.5,
                    }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 flex items-center justify-center rounded-xl shrink-0"
                          style={{ background: r.available ? `${r.color}15` : 'rgba(255,255,255,0.05)', border: `1px solid ${r.available ? r.color + '40' : 'rgba(255,255,255,0.08)'}` }}
                        >
                          <i className={`${r.icon} text-base`} style={{ color: r.available ? r.color : 'rgba(255,255,255,0.2)' }} />
                        </div>
                        <div>
                          <p className="font-mono text-xs text-white/70">{r.method}</p>
                          <p className="font-mono text-[10px] text-white/30 mt-0.5">{r.note}</p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p
                          className="font-orbitron font-black text-sm"
                          style={{ color: r.available ? (r.price === 'Бесплатно' ? '#39ff14' : r.color) : 'rgba(255,255,255,0.2)' }}
                        >
                          {r.price}
                        </p>
                        {r.available && (
                          <p className="font-mono text-[10px] text-white/30 mt-0.5">{r.time}</p>
                        )}
                      </div>
                    </div>
                  </div>
                ))}

                {/* Best option highlight */}
                {results.some((r) => r.available && r.price === 'Бесплатно') && (
                  <div
                    className="flex items-center gap-2 px-4 py-2.5 rounded-xl"
                    style={{ background: 'rgba(57,255,20,0.06)', border: '1px solid rgba(57,255,20,0.2)' }}
                  >
                    <i className="ri-gift-line text-sm" style={{ color: '#39ff14' }} />
                    <p className="font-mono text-xs" style={{ color: '#39ff14' }}>
                      Отлично! Для вашего заказа доступна бесплатная доставка
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default DeliveryCalculator;
